import {api} from 'app/services/api';
import {changeHeaderText} from 'app/actions/headerActions';
import {catchDataError, checkApiRedirect, RedirectException} from 'app/helpers/apiHelpers';
import {trySettingDefaultDistrict} from 'app/actions/sessionActions';
import {updateHistory} from 'app/actions/historyActions';
import {updateHttpError} from 'app/components/error/ErrorHandlerActions';
import {VideoToPlayDetection} from 'app/models/video/VideoToPlayDetection';
import Playlist from 'app/components/tv/models/Playlist';
import {AxiosResponse} from 'axios';
import {ApiVideo} from 'app/components/tv/apiResources/apiVideo';
import {Dispatch} from 'react';
import TvPlaylistState from 'app/routes/tv/types/TvPlaylistState';
import {
  AddPlaylistVideoAction,
  CurrentVideoUpdateAction,
  TvPlaylistAction,
  PlaylistActionType,
  PlaylistFetchingAction,
  PlaylistUpdateAction,
} from 'app/routes/tv/types/TvPlaylistAction';
import Video from 'app/components/tv/models/video';

const getPlaylist = (slug: string): Promise<AxiosResponse<ApiVideo[]>> => {
  return api.get(`/playlists/${slug}/videos`);
};

function fetchingPlaylist(): PlaylistFetchingAction {
  return {
    type: PlaylistActionType.PLAYLIST_FETCHING,
  };
}

function updatePlaylist(data: {
  playlist: TvPlaylistState['playlist'];
  currentVideo: TvPlaylistState['currentVideo'];
}): PlaylistUpdateAction {
  return {
    type: PlaylistActionType.PLAYLIST_UPDATE,
    data,
  };
}

function addPlaylistVideo(data: Video): AddPlaylistVideoAction {
  return {
    type: PlaylistActionType.ADD_PLAYLIST_VIDEO,
    data,
  };
}

function updateCurrentVideo(data: Video): CurrentVideoUpdateAction {
  return {
    type: PlaylistActionType.CURRENT_VIDEO_UPDATE,
    data,
  };
}

function setPlaylistHeader(dispatch: Dispatch<Action>) {
  dispatch(
    changeHeaderText({
      title: 'Torshow',
      parent: {
        name: 'FuPa.tv',
        entity: 'none',
      },
    })
  );
}

type Action = {type: string; data?: any; value?: any};

interface SsrPlaylistState {
  playlist: ApiVideo[];
  currentVideo: ApiVideo;
  isFetching: boolean;
  ssrLoaded: true;
}

function fetchTvPlaylistSSR(playlistSlug: string, currentVideoSlug: string) {
  return async function (dispatch: Dispatch<Action>, getState: any) {
    setPlaylistHeader(dispatch);
    try {
      let response = await getPlaylist(playlistSlug);
      const data = response.data;
      const playlist = Playlist.createByApi(data);
      const playlistSlugFromApi = playlist.getPlaylistSlug();
      checkApiRedirect(playlistSlugFromApi, playlistSlug, null, null, getState);
      const currentVideo = new VideoToPlayDetection(playlist).handle(currentVideoSlug);
      const state: SsrPlaylistState = {
        playlist: data,
        currentVideo,
        isFetching: false,
        ssrLoaded: true,
      };
      dispatch(
        updateHistory('undefined', {
          store: 'TvPlaylistPage',
          data: state,
        })
      );
      if (data.length) {
        // Return the Promise to make sure further handlers wait until it settles -> ssr rendered page is sent after setDefaultDistrict has finished
        return trySettingDefaultDistrict(playlist.getDistrictEntity(), dispatch, getState);
      }
    } catch (error) {
      if (error instanceof RedirectException) {
        throw error;
      } else {
        const errorData = catchDataError(error);
        dispatch(updateHttpError(errorData));
      }
    }
  };
}

function fetchTvPlaylist(
  playlistSlug: string,
  currentVideoSlug: string,
  dispatchRedux: Dispatch<Action>,
  getState: any
) {
  return async function (dispatch: Dispatch<TvPlaylistAction>, state: TvPlaylistState) {
    setPlaylistHeader(dispatchRedux);
    if (state.playlist?.hasVideos()) {
      return;
    }
    try {
      dispatch(fetchingPlaylist());
      const response = await getPlaylist(playlistSlug);
      const data = response.data;
      const playlist = Playlist.createByApi(data);
      const playlistSlugFromApi = playlist.getPlaylistSlug();
      checkApiRedirect(playlistSlugFromApi, playlistSlug, null, null);
      // search for video in playlist, that is passed with the url param
      const currentVideo = new VideoToPlayDetection(playlist).handle(currentVideoSlug);
      dispatch(updatePlaylist({playlist, currentVideo}));
      if (data.length) {
        return trySettingDefaultDistrict(playlist.getDistrictEntity(), dispatchRedux, getState);
      }
    } catch (error) {
      if (error instanceof RedirectException) {
        throw error;
      } else {
        const errorData = catchDataError(error);
        dispatchRedux(updateHttpError(errorData));
      }
    }
  };
}

export {fetchTvPlaylistSSR, fetchTvPlaylist, updateCurrentVideo, addPlaylistVideo};
